/**
 * واژه‌نامه‌ی آدرس در سربرگِ «آدرس‌ها» (WHOSUP-245).
 *
 * جدولِ `crawler.alias_entries` — جفت‌هایی مثلِ «street = st» که کرالر پر
 * می‌کند — با شمارِ استفاده‌ی هر ردیف و کلیدِ روشن/خاموش.
 *
 * ═══ چرا صفحه بلند می‌گوید «هنوز اثری ندارد» ═══
 *
 * تا امروز هیچ بخشی از کرالر این جدول را برای درست کردنِ آدرس نمی‌خواند
 * (فهرستی که واقعاً می‌خوانَد `registry/lexicon.yaml` است)، شمارنده‌ای ندارد،
 * و خودِ جدول فقط روی base پر است و به دیتابیسی که ادمین به آن وصل است
 * همگام نمی‌شود. سهمِ کرالر در `plan/prompts/crawler-alias-usage.md` است.
 * تا آن نرسیده، کلیدی که هیچ کاری نمی‌کند ولی بی‌صدا «ذخیره شد» می‌گوید
 * دروغ است؛ پس این‌جا هر کدام از آن سه واقعیت که هنوز برقرار است، روی صفحه
 * نوشته می‌شود.
 *
 * ═══ فهرست فقط جفت است (WHOSUP-266) ═══
 *
 * ردیف‌های «آدرسِ کامل» در فهرست نیستند؛ با زدن روی هر جفت، آن‌هایی که همان
 * تفاوت را دارند به‌عنوانِ مثال باز می‌شوند، و زیرشان استفاده‌های ثبت‌شده در
 * `crawler.alias_usage`.
 *
 * تشخیصِ ستون‌ها، جدا کردنِ جفت از آدرسِ کامل و تطبیقِ مثال‌ها در
 * `core/lib/aliasReport.ts` است؛ این فایل فقط رندر می‌کند.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsRepo } from '@/core/data/opsRepo';
import { formatDateTime } from '@/core/lib/dates';
import { describeError, reportError } from '@/core/lib/report';
import {
  DECIDER_LABEL,
  VERDICTS,
  VERDICT_HELP,
  VERDICT_LABEL,
  buildAliasReport,
  filterAliasRows,
  type AliasFilter,
  type AliasRow,
} from '@/core/lib/aliasReport';
import { Hint } from '@/ui/Hint';
import { fmtNum as fmt } from '@/ui/Num';

const PAGE = 50;

function Cell({ value }: { value: string }) {
  if (!value) return <span className="dim">—</span>;
  const flat = value.replace(/\s+/g, ' ').trim();
  return (
    <span className="mono ltr" title={flat}>
      {flat.length > 40 ? `${flat.slice(0, 40)}…` : flat}
    </span>
  );
}

function Toggle({ r, busy, onSet }: {
  r: AliasRow; busy: boolean; onSet: (on: boolean) => void;
}) {
  const why = r.key == null
    ? 'جدولِ کرالر کلیدِ اصلی ندارد، پس این ردیف را نمی‌شود پایدار نشانی داد.'
    : r.switched && r.switchedAt
      ? `آخرین تغییر: ${formatDateTime(r.switchedAt)}`
      : 'دست‌نخورده — پیش‌فرض روشن است.';
  return (
    <label className="markcell" title={why}>
      <input type="checkbox" checked={r.enabled} disabled={r.key == null || busy}
        onChange={(e) => onSet(e.target.checked)} />
      {r.enabled ? null : <span className="err">خاموش</span>}
    </label>
  );
}

/** «court = ct»، یا «canada — اختیاری» وقتی طرفِ دوم خالی است. */
function PairText({ pair }: { pair: NonNullable<AliasRow['pair']> }) {
  return (
    <>
      <span className="mono ltr">{pair.a}</span>
      {pair.b == null
        ? <span className="dim"> — اختیاری</span>
        : <> = <span className="mono ltr">{pair.b}</span></>}
    </>
  );
}

const EXAMPLES = 10;

/**
 * بازشده‌ی یک جفت: آدرس‌های کاملی که همین تفاوت را دارند، و بعد
 * استفاده‌های ثبت‌شده در `crawler.alias_usage` (تنبل، مایگریشن 0156).
 */
function PairDetail({ r }: { r: AliasRow }) {
  const [n, setN] = useState(EXAMPLES);
  const usage = useQuery({
    queryKey: ['aliasUsage', r.rawHash],
    queryFn: () => opsRepo.aliasUsage(r.rawHash as string),
    enabled: r.rawHash != null,
  });
  const exact = r.examples.filter((e) => e.exact).length;

  return (
    <div className="aliasdetail">
      <h3>
        <Hint text={`آدرس‌های کامل با همین تفاوت · ${fmt(r.examples.length)}`}
          help="جفت‌های «آدرسِ کامل» در همین واژه‌نامه که کرالر یکی دانسته و یک طرفشان این واژه را دارد و طرفِ دیگر جفتش را. «دقیق» یعنی با همین یک جایگزینی دو آدرس عیناً یکی می‌شوند؛ بقیه جز این، فرقِ دیگری هم دارند." />
      </h3>
      {r.examples.length === 0 ? (
        <p className="dim">هیچ جفتِ آدرسِ کاملی این تفاوت را ندارد.</p>
      ) : (
        <>
          <p className="dim">{fmt(exact)} تا دقیق، {fmt(r.examples.length - exact)} تا با فرقِ دیگر.</p>
          <table className="tbl">
            <tbody>
              {r.examples.slice(0, n).map((e, i) => (
                <tr key={e.key ?? `e${i}`}>
                  <td>{e.exact
                    ? <span className="chip">دقیق</span>
                    : <span className="chip warn">با فرقِ دیگر</span>}</td>
                  <td className="mono ltr">{e.a}</td>
                  <td className="mono ltr">{e.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {r.examples.length > n ? (
            <button type="button" className="linkbtn" onClick={() => setN((k) => k + EXAMPLES * 5)}>
              {fmt(Math.min(EXAMPLES * 5, r.examples.length - n))} مثالِ دیگر
            </button>
          ) : null}
        </>
      )}

      <h3 style={{ marginTop: 12 }}>
        <Hint text="استفاده در کرالر"
          help="crawler.alias_usage: هر آدرسی که این مدخل تمیزش کرد، یک بار. display یعنی در متنی که به کاربر نشان داده می‌شود، identity یعنی در تشخیصِ تکراری‌ها." />
      </h3>
      {r.rawHash == null ? (
        <p className="dim">این ردیف ستونِ raw_hash ندارد، پس به جدولِ استفاده وصل نمی‌شود.</p>
      ) : usage.isLoading ? (
        <p className="dim">…</p>
      ) : usage.error ? (
        <p className="err">{describeError(usage.error)}</p>
      ) : !usage.data?.exists ? (
        <p className="dim">این دیتابیس جدولِ <span className="mono ltr">crawler.alias_usage</span> را ندارد.</p>
      ) : usage.data.total === 0 ? (
        <p className="dim">
          هنوز هیچ استفاده‌ای ثبت نشده — کرالر هنوز این جدول را پر نمی‌کند
          (<span className="mono ltr">plan/prompts/crawler-alias-usage.md</span>).
        </p>
      ) : (
        <>
          <p className="dim">
            {fmt(usage.data.total)} بار
            {Object.entries(usage.data.by_lens).map(([k, v]) => ` · ${k}: ${fmt(v)}`).join('')}
            {usage.data.total > usage.data.rows.length ? ` — آخرین ${fmt(usage.data.rows.length)} تا:` : ''}
          </p>
          <table className="tbl">
            <tbody>
              {usage.data.rows.map((u, i) => (
                <tr key={i}>
                  <td className="mono ltr">{u.lens ?? '—'}</td>
                  <td className="mono ltr">{u.subject ?? '—'}</td>
                  <td className="num">{u.last_used_at ? formatDateTime(u.last_used_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function AliasGlossary() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['aliasEntries'], queryFn: () => opsRepo.aliasEntries() });
  const [filter, setFilter] = useState<AliasFilter>({ q: '', verdict: 'all', status: 'all' });
  const [limit, setLimit] = useState(PAGE);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const rep = useMemo(() => (q.data ? buildAliasReport(q.data) : null), [q.data]);
  const shown = useMemo(
    () => (rep ? filterAliasRows(rep.rows, filter) : []),
    [rep, filter],
  );

  const toggle = useMutation({
    mutationFn: (v: { key: string; enabled: boolean }) => opsRepo.setAliasEnabled(v.key, v.enabled),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: ['aliasEntries'] });
    },
    onError: (e: unknown) => {
      setErr(describeError(e));
      reportError('admin.setAliasEnabled', e);
    },
  });

  const title = (
    <h2>
      <Hint text="واژه‌نامه‌ی آدرس"
        help="جدولِ crawler.alias_entries: جفت‌هایی که کرالر برای یکسان‌سازیِ آدرس نگه می‌دارد («street = st»، «EY Centre = Cohere Centre»). هر جفت یک حکم دارد: یکی‌اند / جزئی‌تر / فرق دارند." />
    </h2>
  );

  if (q.isLoading) return <div className="card">{title}<p className="dim">…</p></div>;
  if (q.error) {
    return <div className="card">{title}<p className="err">{describeError(q.error)}</p></div>;
  }
  if (!rep) return null;

  if (rep.state !== 'ready') {
    return (
      <div className="card">
        {title}
        <p className="dim" style={{ marginTop: 10 }}>
          {rep.state === 'missing' ? (
            <>
              این دیتابیس جدولِ <span className="mono ltr">crawler.alias_entries</span> را
              ندارد. آن جدول مالِ مخزنِ event_crawler است.
            </>
          ) : (
            <>
              جدولِ <span className="mono ltr">crawler.alias_entries</span> این‌جا خالی
              است. کرالر آن را روی base پر می‌کند ولی هنوز به این دیتابیس همگامش
              نمی‌کند — سهمِ کرالر در <span className="mono ltr">plan/prompts/crawler-alias-usage.md</span>.
            </>
          )}
        </p>
      </div>
    );
  }

  const rows = shown.slice(0, limit);

  return (
    <div className="card">
      {title}

      {/* تا وقتی کرالر شمارنده ندارد، یعنی هنوز این جدول را نمی‌خواند. */}
      {!rep.usageTracked ? (
        <p className="err" style={{ marginTop: 8 }}>
          هنوز هیچ بخشی از کرالر این جدول را برای درست کردنِ آدرس نمی‌خوانَد، پس
          ستونِ «استفاده» خالی است و روشن/خاموش کردن فعلاً <b>هیچ اثری</b> روی
          آدرس‌ها ندارد — فقط ذخیره می‌شود تا کرالر وقتی خواندنش را شروع کرد
          رعایتش کند. فهرستی که امروز آدرس را درست می‌کند
          {' '}<span className="mono ltr">registry/lexicon.yaml</span> است.
        </p>
      ) : null}

      <div className="grid n4" style={{ marginTop: 10 }}>
        <div className="card stat">
          <div className="n">{fmt(rep.rows.length)}</div>
          <div className="k">
            <Hint text="جفت"
              help={rep.fullValues > 0
                ? `واژه‌ها، مثلِ «court = ct». ${fmt(rep.fullValues)} ردیفِ دیگرِ جدول دو آدرسِ کامل‌اند که کرالر یکی دانسته؛ آن‌ها در فهرست نیستند و فقط به‌عنوانِ مثال زیرِ جفتی می‌آیند که همان تفاوت را دارند — ${fmt(rep.fullValuesUnmatched)} تاشان زیرِ هیچ جفتی نمی‌نشینند.`
                : 'واژه‌ها، مثلِ «court = ct».'} />
          </div>
        </div>
        <div className={`card stat${rep.disabled > 0 ? ' warn' : ''}`}>
          <div className="n">{fmt(rep.disabled)}</div>
          <div className="k">
            <Hint text="خاموش"
              help={`${fmt(rep.enabled)} ردیف روشن‌اند. خاموش یعنی کرالر نباید برای اصلاحِ آدرس به کارش ببرد.`} />
          </div>
        </div>
        <div className="card stat">
          <div className="n">{rep.usedRows == null ? '—' : fmt(rep.usedRows)}</div>
          <div className="k">
            <Hint text="به کار رفته"
              help="ردیف‌هایی که دست‌کم یک بار آدرسی را اصلاح کرده‌اند. «—» یعنی کرالر هنوز شمارنده (times_used) ندارد — که با «صفر بار» یکی نیست." />
          </div>
        </div>
        <div className="card stat">
          <div className="n">{rep.totalUses == null ? '—' : fmt(rep.totalUses)}</div>
          <div className="k">کلِ اصلاح‌ها</div>
        </div>
      </div>

      <div className="grid n3" style={{ marginTop: 12 }}>
        <div>
          <h3>حکم</h3>
          <table className="tbl">
            <tbody>
              {rep.byVerdict.map((v) => (
                <tr key={v.verdict}>
                  <td><Hint text={VERDICT_LABEL[v.verdict]} help={VERDICT_HELP[v.verdict]} /></td>
                  <td className="num">{fmt(v.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h3>
            <Hint text="تصمیم از"
              help="چه کسی حکم را داده. مسیرِ تطبیق فقط حکمِ آدم و فایلِ seed را قبول دارد؛ مسیرِ نمایش هر ردیفِ «یکی‌اند» را." />
          </h3>
          <table className="tbl">
            <tbody>
              {rep.byDecider.map((d) => (
                <tr key={d.decider}>
                  <td>{d.decider === 'none' ? 'نامعلوم' : DECIDER_LABEL[d.decider]}</td>
                  <td className="num">{fmt(d.n)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="dim">
          {rep.orphanSwitches > 0 ? (
            <p className="err">
              {fmt(rep.orphanSwitches)} کلیدِ روشن/خاموش روی هیچ ردیفی نمی‌نشیند —
              ردیفش در کرالر پاک شده یا کلیدش عوض شده.
            </p>
          ) : null}
          {rep.verdictCol == null ? (
            <p>ستونِ حکم پیدا نشد: هیچ ستونی فقط same / narrower / different ندارد.</p>
          ) : null}
          {rep.shown < rep.total ? (
            <p className="err">فقط {fmt(rep.shown)} از {fmt(rep.total)} ردیف آمده.</p>
          ) : null}
        </div>
      </div>

      <div className="filters" style={{ marginTop: 12, marginBottom: 8 }}>
        <input type="search" placeholder="جست‌وجو…" value={filter.q}
          onChange={(e) => { setFilter({ ...filter, q: e.target.value }); setLimit(PAGE); }} />
        <select value={filter.verdict}
          onChange={(e) => { setFilter({ ...filter, verdict: e.target.value as AliasFilter['verdict'] }); setLimit(PAGE); }}>
          <option value="all">همه‌ی حکم‌ها</option>
          {[...VERDICTS, 'none' as const].map((v) => (
            <option key={v} value={v}>{VERDICT_LABEL[v]}</option>
          ))}
        </select>
        <select value={filter.status}
          onChange={(e) => { setFilter({ ...filter, status: e.target.value as AliasFilter['status'] }); setLimit(PAGE); }}>
          <option value="all">روشن و خاموش</option>
          <option value="enabled">فقط روشن</option>
          <option value="disabled">فقط خاموش</option>
        </select>
        <span className="spacer" style={{ flex: 1 }} />
        <span className="dim">{fmt(shown.length)} ردیف</span>
      </div>

      {err ? <p className="err" style={{ marginBottom: 8 }}>{err}</p> : null}

      {shown.length === 0 ? (
        <p className="dim">هیچ ردیفی با این فیلتر نیست.</p>
      ) : (
        <>
          <div className="scrollx">
            <table className="tbl aliastbl">
              <thead>
                <tr>
                  <th><Hint text="روشن" help="خاموش کردن یعنی کرالر این جفت را برای اصلاحِ آدرس به کار نبرد. ردیف پاک نمی‌شود." /></th>
                  <th><Hint text="جفت" help="روی جفت بزن تا آدرس‌های کاملی که همین تفاوت را دارند و استفاده‌هایش در کرالر باز شوند." /></th>
                  <th><Hint text="مثال" help="چند جفتِ آدرسِ کامل همین تفاوت را دارند." /></th>
                  {rep.columns.map((c) => <th key={c} className="mono ltr">{c}</th>)}
                  <th>حکم</th><th>تصمیم از</th>
                  <th><Hint text="استفاده" help="چند بار این ردیف آدرسی را اصلاح کرده (times_used)." /></th>
                  <th>آخرین بار</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const id = r.key ?? `i${i}`;
                  const isOpen = open === id;
                  return (
                  <React.Fragment key={id}>
                  <tr className={isOpen ? 'aliasrow open' : 'aliasrow'}>
                    <td>
                      <Toggle r={r} busy={toggle.isPending}
                        onSet={(on) => r.key != null && toggle.mutate({ key: r.key, enabled: on })} />
                    </td>
                    <td>
                      <button type="button" className="linkbtn" aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : id)}>
                        {isOpen ? '▾ ' : '▸ '}
                        {r.pair ? <PairText pair={r.pair} /> : <span className="dim">جزئیات</span>}
                      </button>
                    </td>
                    <td className="num">{r.examples.length > 0
                      ? fmt(r.examples.length) : <span className="dim">—</span>}</td>
                    {rep.columns.map((c) => <td key={c}><Cell value={r.cells[c]} /></td>)}
                    <td>{r.verdict
                      ? <span className={`chip${r.verdict === 'different' ? ' warn' : ''}`}>{VERDICT_LABEL[r.verdict]}</span>
                      : <span className="dim">{VERDICT_LABEL.none}</span>}</td>
                    <td>{r.decider ? DECIDER_LABEL[r.decider] : <span className="dim">—</span>}</td>
                    <td className="num">{r.used == null ? <span className="dim">—</span> : fmt(r.used)}</td>
                    <td className="num">{r.lastUsed ? formatDateTime(r.lastUsed) : <span className="dim">—</span>}</td>
                  </tr>
                  {isOpen ? (
                    <tr className="aliasopen">
                      <td colSpan={rep.columns.length + 7}><PairDetail r={r} /></td>
                    </tr>
                  ) : null}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {shown.length > rows.length ? (
            <button type="button" className="linkbtn" style={{ marginTop: 8 }}
              onClick={() => setLimit((n) => n + PAGE * 4)}>
              {fmt(Math.min(PAGE * 4, shown.length - rows.length))} ردیفِ دیگر
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
